import React from 'react'
import { UpdateOrderRequest } from './content-service'

export type SortDirection = 'asc' | 'desc'
export type SortBy = 'title' | 'status' | 'type' | 'updated'
export type FilterBy = 'Status' | 'Type' | 'Topics'

export type ContentType =
  | 'CollegeOverview'
  | 'Academics'
  | 'Admissions'
  | 'CostAndFinancialAid'
  | 'StudentLife'
  | 'Sessions'
  | 'Programs'

export type ProgramItemType = {
  AcademicFocus?: string[]
  AdmissionDescription?: string
  CostDescription?: string
  CreatedDate?: string
  CreditsAwarded?: false
  Description?: string
  Eligibility?: string[]
  FinancialAidAvailable?: boolean
  Id: string
  ImageBucket?: string
  ImageFilename?: string
  ImageKey?: string
  ImageMime?: string
  ImageSize?: number
  ImageUrl?: string
  IsHidden?: boolean
  IsVirtual: boolean
  Link?: string
  MeetingLocation?: string
  RoomAndBoardAvailable?: boolean
  Scid?: number
  Season?: string[]
  SeasonDescription?: string
  Title?: string
  Type?: 'program'
  UpdatedDate?: string
  VideoLink?: string
}

export type ContentItemType = {
  AcademicFocus?: string[]
  CollegeCity?: string
  CollegeCountry?: string
  CollegeName?: string
  CollegeState?: string
  CreatedDate?: string
  Date?: string
  Description?: string
  EndTime?: string
  Id: string
  ImageBucket?: string
  ImageFilename?: string
  ImageKey?: string
  ImageMime?: string
  ImageSize?: number
  ImageUrl?: string
  IsHidden?: boolean
  Link?: string
  Scid?: number
  StartTime?: string
  Title?: string
  Tags?: string[]
  UpdatedDate?: string
}

export type ContentItemObject =
  | (Omit<ProgramItemType, 'Id'> & {
      ContentType: ContentType | undefined
      [key: string]: any
    })
  | (Omit<ContentItemType, 'Id'> & {
      ContentType: ContentType | undefined
      [key: string]: any
    })

export type ContentItem = {
  academicFocus: string[]
  sortIndex: number
  contentType: ContentType
  description: string
  id: string
  image: string
  isFeatured: boolean
  isHidden: boolean
  lastUpdated: string
  tags: string[]
  title: string
}
export type Content = {
  items: ContentItem[]
  Tags: {
    AcademicFocus: {
      [key: string]: boolean
    }
    Categories: {
      [key: string]: {
        [key: string]: boolean
      }
    }
  }
  total: number
}

export type TopicFilter = {
  AcademicFocus: string[]
  Categories: string[]
}
export type BulkContentItem = {
  id: string
  contentType: ContentType
}

interface IContext {
  scid?: number
  // Highlights
  fetchHighlights: () => void
  saveHighlights: (highlights: string[]) => void
  highlights?: string[]
  fetchHighlightsLoading: boolean
  saveHighlightsSucceeded: boolean
  saveHighlightsError: boolean
  // Edit/Create Content
  focusedContentItem?: ContentItemObject
  setFocusedContentItem: (item?: ContentItemObject) => void
  handleFocusedItemType: (type?: ContentType) => void
  fetchContentItemDetails: (id: string, type: string) => void
  saveContentLoading: boolean
  saveContentSucceeded: boolean
  saveContentError: boolean
  // Content
  fetchContent: () => void
  onSaveContentItem: (type: ContentType, item: ContentItemObject, originalItem: ContentItemObject) => void
  onUpdateContentVisibility: (item: BulkContentItem, isHidden: boolean) => void
  onDeleteContentItem: (item: BulkContentItem) => void
  content?: Content
  fetchContentLoading: boolean
  //Reorder
  reorderContent?: Content
  fetchReorderContent: () => void
  fetchReorderContentLoading: boolean
  saveNewOrder: (args: UpdateOrderRequest & { onError: () => void }) => void
  saveNewOrderError: boolean

  // Content Filtering
  filterByStatus?: boolean
  filterByType?: string[]
  filterByTopics?: TopicFilter
  handleTableFiltering: (filter?: FilterBy, value?: boolean | string[] | TopicFilter) => void
  // Content Sorting
  order?: SortDirection
  orderBy?: string
  handleTableSorting: (key: SortBy, dir: SortDirection) => void
  // Content Pagination
  from: number
  setFrom: (from: number) => void
  pageSize: number
  setPageSize: (pageSize: number) => void
  // Bulk Edit
  selectedContent?: BulkContentItem[]
  onSelectContent: (checked: boolean, item: BulkContentItem) => void
  onSelectAllContent: (checked: boolean) => void
  onBulkUpdateContentVisibility: (isHidden: boolean) => void
  onBulkDeleteContent: () => void
}

const ctx: IContext = {
  fetchHighlights: () => {},
  saveHighlights: () => {},
  fetchReorderContent: () => {},
  fetchReorderContentLoading: false,
  saveNewOrder: () => {},
  saveNewOrderError: false,
  fetchHighlightsLoading: true,
  saveHighlightsSucceeded: false,
  saveHighlightsError: false,
  setFocusedContentItem: () => {},
  handleFocusedItemType: () => {},
  fetchContentItemDetails: () => {},
  fetchContent: () => {},
  onSaveContentItem: () => {},
  saveContentLoading: false,
  saveContentSucceeded: false,
  saveContentError: false,
  onUpdateContentVisibility: () => {},
  onDeleteContentItem: () => {},
  fetchContentLoading: true,
  handleTableFiltering: () => {},
  handleTableSorting: () => {},
  from: 0,
  setFrom: () => {},
  pageSize: 5,
  setPageSize: () => {},
  onSelectContent: () => {},
  onSelectAllContent: () => {},
  onBulkUpdateContentVisibility: () => {},
  onBulkDeleteContent: () => {},
}

export default React.createContext(ctx)
